<template>
  <div id="printable">
    <div v-if="isDataLoading">
      {{ $t('pages.loading') }}
    </div>
    <v-table
      v-else
      :row-active-id="rowActiveId"
      :data="tableData"
      :columns="columns"
      clickable
      selectable
      @row-click="onRowClick"
      @row-dblclick="openCurrentMail"
    >
      <template v-slot:column-time="item">
        <p
          v-if="item.row.isNew"
          :class="$style.new"
        >
          NEW
        </p>
        {{ item.row.date | dateTime }}
      </template>
      <template v-slot:column-dateSend="item">
        {{ item.row.attachments.length ? '📎 ' : '' }} {{ item.row.dateSend }}
      </template>
      <template v-slot:column-subject="item">
        <contextmenu :buttons="getContextmenuButtons(item.row.id)">
          <span :class="$style.dangerous">
            <dangerous :value="item.row.statusDangerous" />
          </span>
          <span>{{ item.row.subject }}</span>
        </contextmenu>
      </template>
    </v-table>
    <additional
      v-if="activeRow"
      v-bind="activeRow"
      :open-mail="openCurrentMail"
    />
    <web-mail-modal
      v-if="showEmail"
      :is-email="true"
      :screenshots="tableData"
      :computer-id="computerId"
      :record-id="rowActiveId"
      @close="showEmail = false"
    />
  </div>
</template>

<script>
  import VTable from '@/components/common/table/table.vue';
  import { dateTime } from '@/helpers/filters';
  import Dangerous from '@/components/common/dangerous/index.vue';
  import { LogData } from '@/domains/log/data';
  import Contextmenu from '@/components/common/contextmenu/index.vue';
  import workerBasedRequestMixin from '@/components/common/logs/mixins/workerBasedRequestMixin';
  import WebMailModal from '@/components/pages/computers/log/screenshots/components-default/modal.vue';
  import { type, openMail } from './utils';
  import Additional from './components-default/additional.vue';

  export default {
    components: {
      VTable,
      Additional,
      Dangerous,
      Contextmenu,
      WebMailModal,
    },
    filters: {
      dateTime,
      type,
    },
    mixins: [workerBasedRequestMixin],
    data() {
      return {
        tableData: [],
        computerId: null,
        columns: [
          {
            field: 'time',
            title: this.$t('pages.columns.time'),
            sortFn: (isAsc) => (a, b) => (new Date(a.date) - new Date(b.date)) * (isAsc ? 1 : -1),
          },
          { field: 'dateSend', title: this.$t('pages.columns.dateSend') },
          { field: 'subject', title: this.$t('pages.columns.subject') },
          { field: 'from', title: this.$t('pages.columns.from') },
          { field: 'to', title: this.$t('pages.columns.to') },
          { field: 'username', title: this.$t('pages.columns.username') },
        ],
        showEmail: false,
      };
    },
    computed: {
      rowActiveId() {
        return parseInt(this.$route.params.record, 10);
      },
      activeRow() {
        const activeRow = this.pagedData.find((i) => i?.id === this.rowActiveId);
        return activeRow || null;
      },
    },
    watch: {
      '$route.query': function (query, oldQuery) {
        // eslint-disable-line func-names
        if (query.dateFrom !== oldQuery.dateFrom || query.dateTo !== oldQuery.dateTo) {
          this.fetch();
        }
        if (
          query.filter !== oldQuery.filter ||
          query.mailType !== oldQuery.mailType ||
          query.attachmentOnly !== oldQuery.attachmentOnly ||
          query.domain !== oldQuery.domain
        ) {
          this.getTableData();
        }
      },
      pagedData() {
        this.getTableData();
      },
    },
    async created() {
      this.$store.commit('logs/printData/setColumns', this.columns);
      await this.fetch();
    },
    methods: {
      getTableData() {
        const { attachmentOnly } = this.$route.query;
        const { domain } = this.$route.query;
        const { mailType } = this.$route.query;
        let items = [...this.pagedData];
        items = items.filter((element) => element !== undefined);

        if (items.length > 0) {
          if (attachmentOnly) items = items.filter((item) => item.attachments.length > 0);

          if (domain) {
            if (mailType === this.$t('pages.onlyExternal')) {
              items = items.filter(
                (item) =>
                  (item.direction === 32 && !item.to.includes(domain)) ||
                  (item.direction === 31 && !item.from.includes(domain)),
              );
            } else if (mailType === this.$t('pages.onlyInternal')) {
              items = items.filter(
                (item) =>
                  (item.direction === 32 && item.to.includes(domain)) ||
                  (item.direction === 31 && item.from.includes(domain)),
              );
            }
          }

          this.tableData = LogData.filterTable(
            items,
            [(item) => dateTime(item.date), (item) => dateTime(item.dateSend), 'subject', 'from', 'to', 'username'],
            this.$route.query.filter,
          );
        }
      },
      onRowClick(id) {
        this.$router.push({
          name: 'computers-computer-log-record',
          params: { record: id },
          query: this.$route.query,
        });
      },
      openCurrentMail() {
        this.showEmail = true;
      },
      getContextmenuButtons(id) {
        return [
          {
            label: this.$t('global.save'),
            onClick: () => openMail(this.computerId, id),
          },
        ];
      },
    },
  };
</script>

<style lang="scss" module>
  .new {
    display: inline-block;
    background: $light-blue;
    color: $blue;
    font-size: 7px;
    padding: 3px;
    margin-right: 8px;
    border-radius: 1px;
  }

  .dangerous {
    display: inline-block;
    margin-right: 8px;
  }

  .iframe {
    width: 100%;
    height: 600px;
  }
</style>
